import { Currency, CurrencyAmount, JSBI, Token, TokenAmount } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { useBalance } from 'wagmi'
import ERC20_INTERFACE from '../../constants/abis/erc20'
import { useAllTokens } from '../../hooks/Tokens'
import { useActiveWeb3React } from '../../hooks'
import { isAddress } from '../../utils'
import { useMultipleContractSingleData, EthersInterface } from '../multicall/hooks'

/**
 * Returns a map of the given addresses to their eventually consistent ETC balances.
 * Uses wagmi's useBalance hook for direct RPC calls (eth_getBalance).
 * Note: ETC mainnet multicall doesn't have getEthBalance, so we can't use multicall for this.
 */
export function useETHBalances(
  uncheckedAddresses?: (string | undefined)[]
): { [address: string]: CurrencyAmount | undefined } {
  // Get the first valid address (this hook is only ever called with 0 or 1 addresses)
  const address = useMemo(() => {
    if (!uncheckedAddresses || uncheckedAddresses.length === 0) return undefined
    const checked = isAddress(uncheckedAddresses[0])
    return checked || undefined
  }, [uncheckedAddresses])

  // Use wagmi's useBalance for direct eth_getBalance RPC call
  const { data: balanceData } = useBalance({
    address: address as `0x${string}` | undefined,
  })

  return useMemo(() => {
    if (!address || !balanceData) return {}
    return {
      [address]: CurrencyAmount.ether(JSBI.BigInt(balanceData.value.toString()))
    }
  }, [address, balanceData])
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[]
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [],
    [tokens]
  )

  const validatedTokenAddresses = useMemo(() => validatedTokens.map(vt => vt.address), [validatedTokens])

  const balances = useMultipleContractSingleData(validatedTokenAddresses, ERC20_INTERFACE as unknown as EthersInterface, 'balanceOf', [address])

  const anyLoading: boolean = useMemo(() => balances.some(callState => callState.loading), [balances])

  return [
    useMemo(
      () =>
        address && validatedTokens.length > 0
          ? validatedTokens.reduce<{ [tokenAddress: string]: TokenAmount | undefined }>((memo, token, i) => {
              const value = balances?.[i]?.result?.[0]
              const amount = value ? JSBI.BigInt(value.toString()) : undefined
              if (amount) {
                memo[token.address] = new TokenAmount(token, amount)
              }
              return memo
            }, {})
          : {},
      [address, validatedTokens, balances]
    ),
    anyLoading
  ]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[]
): { [tokenAddress: string]: TokenAmount | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): TokenAmount | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[]
): (CurrencyAmount | undefined)[] {
  // Use 'address' property check instead of instanceof to avoid module duplication issues
  const tokens = useMemo(() => currencies?.filter((currency): currency is Token => !!currency && 'address' in currency) ?? [], [
    currencies
  ])

  const tokenBalances = useTokenBalances(account, tokens)
  // Use property check instead of reference equality to avoid module duplication issues
  const containsETH: boolean = useMemo(() => currencies?.some(currency => currency && !('address' in currency)) ?? false, [currencies])
  const ethBalance = useETHBalances(containsETH ? [account] : [])

  return useMemo(
    () =>
      currencies?.map(currency => {
        if (!account || !currency) return undefined
        // Use 'address' property check instead of instanceof to avoid module duplication issues
        if ('address' in currency) return tokenBalances[(currency as Token).address]
        // Use property check for native currency (no address) to avoid module duplication issues
        if (!('address' in currency)) return ethBalance[account]
        return undefined
      }) ?? [],
    [account, currencies, ethBalance, tokenBalances]
  )
}

export function useCurrencyBalance(account?: string, currency?: Currency): CurrencyAmount | undefined {
  return useCurrencyBalances(account, [currency])[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: TokenAmount | undefined } {
  const { account } = useActiveWeb3React()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)
  return balances ?? {}
}
